import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Button, IconButton, HStack, VStack, Text, Badge, Table, Thead, Tbody, Tr, Th, Td,
  useToast, Spinner, Flex, Input, Select, Alert, AlertIcon, Divider, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, NumberInput, NumberInputField, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, Textarea, FormControl, FormLabel
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

// Date calendaire locale (YYYY-MM-DD) — contrairement à toISOString() qui
// renvoie toujours la date en UTC, décalant "aujourd'hui" avant/après minuit
// heure de Paris selon l'heure d'été/hiver.
const localDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function BiblicalReading() {
  const toast = useToast();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isExtractOpen, onOpen: onExtractOpen, onClose: onExtractClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isEnvoisOpen, onOpen: onEnvoisOpen, onClose: onEnvoisClose } = useDisclosure();

  const [editingEntry, setEditingEntry] = useState(null);
  const [editReference, setEditReference] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [envoisEntry, setEnvoisEntry] = useState(null);
  const [envois, setEnvois] = useState([]);
  const [loadingEnvois, setLoadingEnvois] = useState(false);

  const [extractYear, setExtractYear] = useState(new Date().getFullYear());
  const [extracting, setExtracting] = useState(false);
  const [draftEntries, setDraftEntries] = useState([]);
  const [unmatchedLines, setUnmatchedLines] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const [program, setProgram] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [previewRef, setPreviewRef] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchProgram = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest(`/biblical-reading/program?month=${selectedMonth}`);
      setProgram(data);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, toast]);

  useEffect(() => { fetchProgram(); }, [fetchProgram]);

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('adminToken');
    const url = `${process.env.REACT_APP_API_URL || '/api'}/biblical-reading/template`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'template_lecture_biblique.xlsx';
        a.click();
      });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/biblical-reading/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur upload');

      toast({
        title: 'Programme importé',
        description: `${data.inserted} ajouté(s), ${data.updated} mis à jour${data.errors.length ? `, ${data.errors.length} erreur(s)` : ''}`,
        status: data.errors.length > 0 ? 'warning' : 'success',
        duration: 6000, isClosable: true,
      });
      fetchProgram();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setUploading(false);
    }
  };

  const handleExtractPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    try {
      setExtracting(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('annee', String(extractYear));
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/biblical-reading/extract-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur extraction');

      setDraftEntries(data.entries.map(en => ({ ...en })));
      setUnmatchedLines(data.unmatched || []);
      onExtractOpen();
      toast({
        title: 'Extraction terminée',
        description: `${data.entries.length} lecture(s) détectée(s) sur ${data.pages} page(s). Vérifiez avant de valider.`,
        status: 'info', duration: 6000, isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Erreur d\'extraction', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setExtracting(false);
    }
  };

  const updateDraftEntry = (idx, field, value) => {
    setDraftEntries(prev => prev.map((en, i) => (i === idx ? { ...en, [field]: value } : en)));
  };
  const removeDraftEntry = (idx) => setDraftEntries(prev => prev.filter((_, i) => i !== idx));
  const addDraftEntry = () => setDraftEntries(prev => [...prev, { date_lecture: '', reference: '', confidence: 'manuel' }]);

  const handleConfirmImport = async () => {
    try {
      setConfirming(true);
      const data = await apiService.makeRequest('/biblical-reading/import-confirm', {
        method: 'POST',
        body: JSON.stringify({
          entries: draftEntries.map(({ date_lecture, reference }) => ({ date_lecture, reference })),
        }),
      });
      toast({
        title: 'Import confirmé',
        description: `${data.inserted} ajouté(s), ${data.updated} mis à jour${data.errors.length ? `, ${data.errors.length} erreur(s)` : ''}`,
        status: data.errors.length > 0 ? 'warning' : 'success',
        duration: 6000, isClosable: true,
      });
      onExtractClose();
      fetchProgram();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setConfirming(false);
    }
  };

  const handlePublish = async (dateStr) => {
    try {
      setPublishingId(dateStr);
      await apiService.makeRequest('/biblical-reading/publish', {
        method: 'POST',
        body: JSON.stringify({ date: dateStr }),
      });
      toast({ title: 'Publié !', description: `Lecture du ${formatDate(dateStr)} publiée sur le blog.`, status: 'success', duration: 4000 });
      fetchProgram();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setPublishingId(null);
    }
  };

  const handlePublishToday = () => {
    handlePublish(localDateString());
  };

  const handlePreview = async () => {
    if (!previewRef.trim()) return;
    try {
      setPreviewLoading(true);
      const data = await apiService.makeRequest('/biblical-reading/preview', {
        method: 'POST',
        body: JSON.stringify({ reference: previewRef }),
      });
      setPreviewHtml(data.html);
      onPreviewOpen();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée du programme ?')) return;
    try {
      await apiService.makeRequest(`/biblical-reading/${id}`, { method: 'DELETE' });
      toast({ title: 'Supprimé', status: 'info', duration: 2000 });
      fetchProgram();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const handleOpenEdit = (entry) => {
    setEditingEntry(entry);
    setEditReference(entry.reference);
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    if (!editReference.trim()) return;
    try {
      setSavingEdit(true);
      await apiService.makeRequest(`/biblical-reading/${editingEntry.id}`, {
        method: 'PUT',
        body: JSON.stringify({ reference: editReference.trim() }),
      });
      toast({ title: 'Référence corrigée', status: 'success', duration: 3000 });
      onEditClose();
      setEditingEntry(null);
      fetchProgram();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingEdit(false);
    }
  };

  const [regeneratingId, setRegeneratingId] = useState(null);
  const handleRegenerate = async (entry) => {
    try {
      setRegeneratingId(entry.id);
      const data = await apiService.makeRequest(`/biblical-reading/${entry.id}/regenerate`, { method: 'POST' });
      toast({ title: 'Contenu régénéré', description: `Article #${data.blog_post_id} mis à jour (${data.nb_references} référence(s))`, status: 'success', duration: 4000 });
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleOpenEnvois = async (entry) => {
    setEnvoisEntry(entry);
    onEnvoisOpen();
    try {
      setLoadingEnvois(true);
      const data = await apiService.makeRequest(`/biblical-reading/${entry.id}/envois`);
      setEnvois(data.envois || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoadingEnvois(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = dateStr ? String(dateStr).split('T')[0] : '';
    return new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', {
      weekday: 'short', day: 'numeric', month: 'long'
    });
  };

  const today = localDateString();
  const todayEntry = program.find(p => String(p.date_lecture).split('T')[0] === today);

  return (
    <Box maxW="1100px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">📖 Programme de Lecture Biblique</Heading>
        <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="md">
          Louis Segond 1910
        </Badge>
      </Flex>

      {/* Bandeau publication du jour */}
      {todayEntry && !todayEntry.is_published && (
        <Alert status="info" mb={6} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Lecture du jour non publiée</Text>
            <Text fontSize="sm">{todayEntry.reference}</Text>
          </Box>
          <Button colorScheme="blue" size="sm" ml={4} onClick={handlePublishToday}
            isLoading={publishingId === today}>
            Publier maintenant
          </Button>
        </Alert>
      )}

      {todayEntry && todayEntry.is_published && (
        <Alert status="success" mb={6} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Text>La lecture du jour est publiée sur le blog.</Text>
          </Box>
          {todayEntry.blog_post_id && (
            <Button
              size="sm" ml={4} colorScheme="whatsapp"
              leftIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              }
              onClick={() => {
                const url = `${window.location.origin}/blog?article=${todayEntry.blog_post_id}`;
                const msg = String.fromCodePoint(0x1F4D6) + ' *Lecture biblique du jour — ' + todayEntry.reference + '*\n\nRetrouvez le passage complet sur notre site :\n' + url;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              }}
            >
              Partager sur WhatsApp
            </Button>
          )}
        </Alert>
      )}

      {/* Outils */}
      <Box bg="white" p={5} borderRadius="md" shadow="sm" mb={6}>
        <Text fontWeight="bold" mb={3}>Importer un programme Excel</Text>
        <HStack spacing={3} flexWrap="wrap">
          <Button size="sm" variant="outline" colorScheme="green" onClick={handleDownloadTemplate}>
            Télécharger le template
          </Button>
          <Button as="label" htmlFor="excel-upload" size="sm" colorScheme="blue"
            isLoading={uploading} cursor="pointer">
            {uploading ? 'Import...' : 'Importer un fichier Excel'}
          </Button>
          <Input id="excel-upload" type="file" accept=".xlsx,.xls" display="none" onChange={handleUpload} />
        </HStack>

        <Divider my={4} />

        <Text fontWeight="bold" mb={1}>Importer un PDF scanné (extraction OCR assistée)</Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          Fonctionne avec les plannings scannés en PDF. Les dates et références sont détectées automatiquement,
          mais doivent être vérifiées avant validation.
        </Text>
        <HStack spacing={3} flexWrap="wrap">
          <Text fontSize="sm">Année :</Text>
          <NumberInput value={extractYear} onChange={(_, v) => setExtractYear(v)} min={2020} max={2100} size="sm" maxW="110px">
            <NumberInputField />
          </NumberInput>
          <Button as="label" htmlFor="pdf-upload" size="sm" colorScheme="purple"
            isLoading={extracting} loadingText="Analyse OCR..." cursor="pointer">
            Importer un PDF
          </Button>
          <Input id="pdf-upload" type="file" accept=".pdf" display="none" onChange={handleExtractPdf} />
        </HStack>

        <Text fontWeight="bold" mb={3}>Aperçu d'une référence</Text>
        <HStack>
          <Input
            placeholder="Ex: Jean 3:16-18 ou Psaumes 23:1-6"
            value={previewRef}
            onChange={e => setPreviewRef(e.target.value)}
            maxW="350px"
            size="sm"
          />
          <Button size="sm" colorScheme="purple" onClick={handlePreview}
            isLoading={previewLoading} isDisabled={!previewRef.trim()}>
            Aperçu
          </Button>
        </HStack>
      </Box>

      {/* Filtre par mois */}
      <HStack mb={4}>
        <Text fontWeight="medium">Mois :</Text>
        <Input
          type="month"
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
          maxW="200px"
          size="sm"
        />
        <Badge colorScheme="gray">{program.length} entrée(s)</Badge>
      </HStack>

      {/* Table du programme */}
      {loading ? (
        <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
      ) : program.length === 0 ? (
        <Box bg="white" p={8} borderRadius="md" shadow="sm" textAlign="center">
          <Text color="gray.500">Aucun programme pour ce mois. Importez un fichier Excel.</Text>
        </Box>
      ) : (
        <Box bg="white" borderRadius="md" shadow="sm" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Date</Th>
                <Th>Référence(s)</Th>
                <Th>Statut</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {program.map(entry => {
                const isToday = entry.date_lecture === today;
                return (
                  <Tr key={entry.id} bg={isToday ? 'blue.50' : undefined}>
                    <Td fontWeight={isToday ? 'bold' : 'normal'}>
                      {formatDate(entry.date_lecture)}
                      {isToday && <Badge ml={2} colorScheme="blue" fontSize="xs">Aujourd'hui</Badge>}
                    </Td>
                    <Td>{entry.reference}</Td>
                    <Td>
                      {entry.is_published
                        ? <Badge colorScheme="green">Publié</Badge>
                        : <Badge colorScheme="orange">En attente</Badge>}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {!entry.is_published && (
                          <Button size="xs" colorScheme="blue" variant="outline"
                            onClick={() => handleOpenEdit(entry)}>
                            Modifier
                          </Button>
                        )}
                        {!entry.is_published && (
                          <Button size="xs" colorScheme="green"
                            onClick={() => handlePublish(entry.date_lecture)}
                            isLoading={publishingId === entry.date_lecture}>
                            Publier
                          </Button>
                        )}
                        {entry.is_published && entry.blog_post_id && (
                          <Badge colorScheme="green" fontSize="xs">Article #{entry.blog_post_id}</Badge>
                        )}
                        {entry.is_published && (
                          <Button size="xs" variant="outline" colorScheme="purple"
                            onClick={() => handleOpenEnvois(entry)}>
                            Envois WhatsApp
                          </Button>
                        )}
                        {entry.is_published && (
                          <Button size="xs" variant="outline" colorScheme="orange"
                            onClick={() => handleRegenerate(entry)}
                            isLoading={regeneratingId === entry.id}
                            title="Régénérer le contenu de l'article à partir de la référence actuelle">
                            Régénérer
                          </Button>
                        )}
                        {!entry.is_published && (
                          <Button size="xs" colorScheme="red" variant="ghost"
                            onClick={() => handleDelete(entry.id)}>
                            ✕
                          </Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal aperçu */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Aperçu — {previewRef}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewClose}>Fermer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal relecture/validation de l'extraction PDF */}
      <Modal isOpen={isExtractOpen} onClose={onExtractClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Vérifier l'extraction avant import</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4} borderRadius="md" fontSize="sm">
              <AlertIcon />
              L'OCR peut se tromper. Vérifiez chaque date et référence avant de valider — rien n'a encore été enregistré.
            </Alert>

            <HStack mb={3} justify="space-between">
              <Badge colorScheme="gray">{draftEntries.length} ligne(s)</Badge>
              <Button size="xs" onClick={addDraftEntry}>+ Ajouter une ligne</Button>
            </HStack>

            <Box borderRadius="md" borderWidth="1px" overflowX="auto" mb={4}>
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Date</Th>
                    <Th>Référence(s)</Th>
                    <Th>Confiance</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {draftEntries.map((entry, idx) => (
                    <Tr key={idx}>
                      <Td>
                        <Input type="date" size="sm" value={entry.date_lecture || ''}
                          onChange={e => updateDraftEntry(idx, 'date_lecture', e.target.value)} />
                      </Td>
                      <Td>
                        <Input size="sm" value={entry.reference || ''}
                          onChange={e => updateDraftEntry(idx, 'reference', e.target.value)} />
                      </Td>
                      <Td>
                        <Badge colorScheme={entry.confidence === 'high' ? 'green' : entry.confidence === 'low' ? 'orange' : 'gray'}>
                          {entry.confidence === 'high' ? 'Fiable' : entry.confidence === 'low' ? 'À vérifier' : 'Manuel'}
                        </Badge>
                      </Td>
                      <Td>
                        <IconButton size="xs" colorScheme="red" variant="ghost" aria-label="Supprimer"
                          icon={<Text>✕</Text>} onClick={() => removeDraftEntry(idx)} />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {unmatchedLines.length > 0 && (
              <Accordion allowToggle>
                <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="sm">
                      Lignes non reconnues par l'OCR ({unmatchedLines.length}) — vérifiez qu'aucune lecture ne manque
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" maxH="200px" overflowY="auto">
                    {unmatchedLines.join('\n')}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExtractClose}>Annuler</Button>
            <Button colorScheme="purple" onClick={handleConfirmImport}
              isLoading={confirming} isDisabled={draftEntries.length === 0}>
              Valider l'import ({draftEntries.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal correction d'une entrée du programme */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Corriger la lecture {editingEntry ? `du ${formatDate(editingEntry.date_lecture)}` : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm">Référence(s) biblique(s)</FormLabel>
              <Textarea
                value={editReference}
                onChange={e => setEditReference(e.target.value)}
                placeholder="Ex: Genèse 1:1-5, Jean 3:16"
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSaveEdit}
              isLoading={savingEdit} isDisabled={!editReference.trim()}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal détail des envois WhatsApp */}
      <Modal isOpen={isEnvoisOpen} onClose={onEnvoisClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Envois WhatsApp {envoisEntry ? `du ${formatDate(envoisEntry.date_lecture)}` : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingEnvois ? (
              <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
            ) : envois.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={6}>Aucun envoi enregistré pour cette lecture.</Text>
            ) : (
              <>
                <HStack mb={3} spacing={3}>
                  <Badge colorScheme="gray">{envois.length} destinataire(s)</Badge>
                  <Badge colorScheme="green">{envois.filter(e => e.succes).length} envoyé(s)</Badge>
                  <Badge colorScheme="red">{envois.filter(e => !e.succes).length} échec(s)</Badge>
                </HStack>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr><Th>Membre</Th><Th>Téléphone</Th><Th>Statut</Th><Th>Détail</Th></Tr>
                  </Thead>
                  <Tbody>
                    {envois.map(e => (
                      <Tr key={e.id}>
                        <Td>{e.prenom} {e.nom}</Td>
                        <Td fontFamily="mono" fontSize="xs">{e.telephone}</Td>
                        <Td>
                          {e.succes
                            ? <Badge colorScheme="green">Envoyé</Badge>
                            : <Badge colorScheme="red">Échec</Badge>}
                        </Td>
                        <Td fontSize="xs" color="gray.500">{e.succes ? '—' : (e.erreur || '')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onEnvoisClose}>Fermer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
